import { Chip, Icon, Typography } from "@platform/design-system";
import { Avatar, Box, ButtonBase, Stack } from "@mui/material";

import type { ThreadSummary } from "../../threadSummary";

export interface ThreadRowProps {
  thread: ThreadSummary;
  expanded: boolean;
  onToggle: (threadId: string) => void;
}

/**
 * One conversation in the list.
 *
 * The whole row is the disclosure control rather than the chevron alone: the
 * design annotates the list as "All threads live within an accordion with
 * expand/collapse functionality", and a 1113px row with a 24px target at its
 * left edge is a worse version of the same behaviour.
 *
 * What the row does not show is as deliberate as what it does. The design
 * puts an unread count, a carrier name, the thread's coverage lines and a
 * RUMI processing state on each row; a thread listing carries none of them,
 * so the row renders what the listing answers with and the rest arrives with
 * the fields that back it.
 */
export const ThreadRow = ({
  thread,
  expanded,
  onToggle,
}: ThreadRowProps): React.JSX.Element => (
  <ButtonBase
    data-testid="thread-row"
    data-thread-id={thread.id}
    aria-expanded={expanded}
    onClick={() => onToggle(thread.id)}
    sx={{
      width: "100%",
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      px: 2,
      py: 1.5,
      textAlign: "left",
      borderBottom: 1,
      borderColor: "divider",
      "&:hover": { backgroundColor: "action.hover" },
    }}
  >
    <Box
      sx={{
        display: "flex",
        pt: 0.5,
        transform: expanded ? "rotate(180deg)" : undefined,
        transition: "transform 150ms",
      }}
    >
      <Icon name="expand" size="sm" />
    </Box>

    <Avatar sx={{ width: 40, height: 40 }}>{thread.initials}</Avatar>

    <Stack sx={{ flexGrow: 1, minWidth: 0 }} spacing={0.25}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography
          data-testid="thread-row-counterparty"
          variant="body-medium"
          color="textSecondary"
        >
          {thread.counterparty}
        </Typography>
        {thread.carrierName && (
          <Typography
            data-testid="thread-row-carrier"
            variant="body-medium"
            color="textSecondary"
          >
            {`· ${thread.carrierName}`}
          </Typography>
        )}
      </Box>
      <Typography data-testid="thread-row-subject" variant="header-small">
        {thread.subject}
      </Typography>
      <Typography
        data-testid="thread-row-activity"
        variant="caption"
        color="textSecondary"
      >
        {thread.activity}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {thread.unreadCount > 0 && (
          <Chip
            // A row is a button, and a chip inside one has to be inline.
            component="span"
            size="small"
            color="attention"
            variant="subtle"
            leadingVisual={false}
            data-testid="thread-row-unread"
            label={`${thread.unreadCount} unread`}
          />
        )}

        {thread.hasRumiDraft && (
          <Chip
            // A row is a button, and a chip inside one has to be inline.
            component="span"
            size="small"
            color="success"
            variant="subtle"
            leadingVisual={false}
            data-testid="thread-row-rumi-draft"
            label="RUMI draft"
          />
        )}

        {thread.tags.length > 0 && (
          <Typography
            data-testid="thread-row-tags"
            variant="caption"
            color="textSecondary"
          >
            {thread.tags.join(" · ")}
          </Typography>
        )}
      </Box>
    </Stack>

    <Box sx={{ flexShrink: 0, pt: 0.5 }}>
      <Chip
        // A row is a button, and a chip inside one has to be inline content.
        component="span"
        size="small"
        color={thread.status.color}
        variant="subtle"
        leadingVisual={false}
        label={thread.status.label}
      />
    </Box>
  </ButtonBase>
);
